'use server'

import { apiRequest, ApiResponse } from '../request'

export type AdmissionStatus =
    | 'ADM_SCHEDULED'
    | 'ADM_ACTIVE'
    | 'ADM_ON_LEAVE'
    | 'ADM_TRANSFERRED'
    | 'ADM_DISCHARGED'
    | 'ADM_CANCELLED'
    | 'ADM_DECEASED'

export type AdmissionType = 'ELECTIVE' | 'URGENCY' | 'EMERGENCY' | 'TRANSFER_IN'

export interface Admission {
    id: number
    companyId: number
    hospitalId: number
    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    unitId: number
    unit?: {
        id: number
        name: string
        type: string
    }
    bedId?: number
    bed?: {
        id: number
        number: string
    }
    healthPlanId?: number
    healthPlan?: {
        id: number
        name: string
    }
    healthPlanNumber?: string
    admissionNumber: string
    admissionType: AdmissionType
    status: AdmissionStatus
    admissionDate: string
    expectedDischargeDate?: string
    actualDischargeDate?: string
    admissionDiagnosis?: string
    cid10Code?: string
    attendingPhysicianId?: number
    attendingPhysician?: {
        id: number
        name: string
    }
    requestingPhysicianId?: number
    notes?: string
    requiresIsolation: boolean
    isolationReason?: string
    allergies?: string
    specialNeeds?: string
    companionName?: string
    companionPhone?: string
    companionRelation?: string
    createdAt: string
    updatedAt: string
}

export interface CreateAdmissionData {
    hospitalId: number
    clientId: number
    unitId: number
    bedId?: number
    healthPlanId?: number
    healthPlanNumber?: string
    admissionType: AdmissionType
    admissionDate?: string
    expectedDischargeDate?: string
    admissionDiagnosis?: string
    cid10Code?: string
    attendingPhysicianId?: number
    requestingPhysicianId?: number
    notes?: string
    requiresIsolation?: boolean
    isolationReason?: string
    allergies?: string
    specialNeeds?: string
    companionName?: string
    companionPhone?: string
    companionRelation?: string
}

export interface UpdateAdmissionData extends Partial<CreateAdmissionData> {
    status?: AdmissionStatus
}

export interface AdmissionFilters {
    hospitalId?: number
    unitId?: number
    status?: AdmissionStatus
    startDate?: string
    endDate?: string
}

export async function getAdmissions(
    companyId?: number,
    filters?: AdmissionFilters
): Promise<ApiResponse<Admission[]>> {
    return apiRequest<Admission[]>('hospital/admissions', {
        params: { companyId, ...filters },
    })
}

export async function getAdmissionsByHospital(
    hospitalId: number,
): Promise<ApiResponse<Admission[]>> {
    return apiRequest<Admission[]>(`hospital/admissions/hospital/${hospitalId}`, {})
}

export async function getAdmission(id: number): Promise<ApiResponse<Admission>> {
    return apiRequest<Admission>(`hospital/admissions/${id}`, {})
}

export async function getActiveAdmissions(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<Admission[]>> {
    return apiRequest<Admission[]>(`hospital/admissions/active/${companyId}`, {
        params: { hospitalId },
    })
}

export async function getAdmissionsByUnit(
    unitId: number
): Promise<ApiResponse<Admission[]>> {
    return apiRequest<Admission[]>(`hospital/admissions/unit/${unitId}`, {})
}

export async function getClientAdmissions(
    clientId: number
): Promise<ApiResponse<Admission[]>> {
    return apiRequest<Admission[]>(`hospital/admissions/client/${clientId}`, {})
}

export async function createAdmission(
    data: CreateAdmissionData
): Promise<ApiResponse<Admission>> {
    return apiRequest<Admission>('hospital/admissions', { data }, 'post')
}

export async function updateAdmission(
    id: number,
    data: UpdateAdmissionData
): Promise<ApiResponse<Admission>> {
    return apiRequest<Admission>(`hospital/admissions/${id}`, { data }, 'patch')
}

export async function transferAdmission(
    id: number,
    data: { unitId: number; bedId?: number; reason?: string }
): Promise<ApiResponse<Admission>> {
    return apiRequest<Admission>(`hospital/admissions/${id}/transfer`, { data }, 'post')
}

export async function deleteAdmission(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/admissions/${id}`, {}, 'delete')
}

export const ADMISSION_STATUS_LABELS: Record<AdmissionStatus, string> = {
    ADM_SCHEDULED: 'Agendada',
    ADM_ACTIVE: 'Ativa',
    ADM_ON_LEAVE: 'Saída Temporária',
    ADM_TRANSFERRED: 'Transferida',
    ADM_DISCHARGED: 'Alta',
    ADM_CANCELLED: 'Cancelada',
    ADM_DECEASED: 'Óbito',
}

export const ADMISSION_STATUS_COLORS: Record<AdmissionStatus, string> = {
    ADM_SCHEDULED: 'badge-info',
    ADM_ACTIVE: 'badge-success',
    ADM_ON_LEAVE: 'badge-warning',
    ADM_TRANSFERRED: 'badge-secondary',
    ADM_DISCHARGED: 'badge-ghost',
    ADM_CANCELLED: 'badge-error',
    ADM_DECEASED: 'badge-neutral',
}

export const ADMISSION_TYPE_LABELS: Record<AdmissionType, string> = {
    ELECTIVE: 'Eletiva',
    URGENCY: 'Urgência',
    EMERGENCY: 'Emergência',
    TRANSFER_IN: 'Transferência',
}
